// Configuración de los polígonos en tarifas y choferes (GET/PUT
// /v2/zones/settings). Arranca con todo como antes: por código postal y sin
// choferes por zona. En las estanterías el polígono gana siempre: eso no se
// configura acá.

import React, { useEffect, useState } from "react";
import Button from "../ui/Button";

export type ZoneSettings = {
  store_tariffs: boolean;
  driver_tariffs: boolean;
  intake_pricing: "postal_code" | "polygon";
  settlement_pricing: "postal_code" | "polygon";
  driver_mode: "off" | "suggest" | "drafts";
  driver_link: "zone" | "shelf" | "zone_first" | "shelf_first";
  updated_by?: string | null;
  updated_at?: string | null;
};

const sectionStyle: React.CSSProperties = { border: "1px solid var(--border)", borderRadius: "16px", padding: "14px", display: "grid", gap: "8px" };
const headStyle: React.CSSProperties = { fontSize: "14px", fontWeight: 600, color: "var(--text-primary)" };
const helpStyle: React.CSSProperties = { fontSize: "12px", color: "var(--text-secondary)", lineHeight: 1.5 };
const optionStyle: React.CSSProperties = { display: "flex", gap: "8px", alignItems: "flex-start", fontSize: "14px", color: "var(--text-primary)", cursor: "pointer" };

function Choice<T extends string>({ name, value, options, onChange, disabled }: {
  name: string; value: T; options: { value: T; label: string; help?: string }[]; onChange: (v: T) => void; disabled?: boolean;
}) {
  return (
    <div style={{ display: "grid", gap: "6px" }}>
      {options.map((o) => (
        <label key={o.value} style={{ ...optionStyle, opacity: disabled ? 0.6 : 1 }}>
          <input type="radio" name={name} value={o.value} checked={value === o.value} disabled={disabled} onChange={() => onChange(o.value)} style={{ marginTop: "3px" }} />
          <span>{o.label}{o.help ? <span style={{ ...helpStyle, display: "block" }}>{o.help}</span> : null}</span>
        </label>
      ))}
    </div>
  );
}

export default function ZoneSettingsForm({ req, onSaved }: { req: (p: string, i?: any) => Promise<any>; onSaved: (s: ZoneSettings) => void }) {
  const [s, setS] = useState<ZoneSettings | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    req("/v2/zones/settings").then((r) => setS(r.settings)).catch((e) => setError(e.message));
  }, [req]);

  if (!s) return error ? <div style={{ color: "var(--danger)" }}>{error}</div> : <span style={helpStyle}>Cargando…</span>;
  const set = (patch: Partial<ZoneSettings>) => setS({ ...s, ...patch });
  const anyTariff = s.store_tariffs || s.driver_tariffs;

  const save = async () => {
    setSaving(true); setError(null);
    try {
      const { updated_by, updated_at, ...body } = s;
      const r = await req("/v2/zones/settings", { method: "PUT", body });
      setS(r.settings);
      onSaved(r.settings);
    } catch (e: any) { setError(e.message); } finally { setSaving(false); }
  };

  return (
    <div style={{ display: "grid", gap: "12px" }}>
      <p style={{ ...helpStyle, margin: 0 }}>
        En las estanterías el polígono gana siempre. Acá se decide si también cuenta para cobrar, para liquidar y para elegir el
        chofer. Con todo como viene, el sistema hace lo mismo que antes de los polígonos.
      </p>

      <div style={sectionStyle}>
        <div style={headStyle}>1 · Polígonos en las tarifas</div>
        <label style={optionStyle}>
          <input type="checkbox" checked={s.store_tariffs} onChange={(e) => set({ store_tariffs: e.target.checked })} />
          <span>Tarifas de tiendas <span style={helpStyle}>(lo que se le cobra a la tienda)</span></span>
        </label>
        <label style={optionStyle}>
          <input type="checkbox" checked={s.driver_tariffs} onChange={(e) => set({ driver_tariffs: e.target.checked })} />
          <span>Tarifas de choferes <span style={helpStyle}>(lo que se le paga al chofer; solo cuenta en la liquidación)</span></span>
        </label>
        <div style={helpStyle}>
          El tier sale del vínculo de la zona con la tarifa (Listas de precios → zonas). Si el envío no cae en ningún polígono de la
          tarifa, decide el CP como siempre.
        </div>
      </div>

      <div style={sectionStyle}>
        <div style={headStyle}>2 · Precio al dar de alta un envío que trae ubicación</div>
        <Choice
          name="intake_pricing" value={s.intake_pricing} disabled={!s.store_tariffs} onChange={(v) => set({ intake_pricing: v })}
          options={[
            { value: "postal_code", label: "Por código postal (como siempre)" },
            { value: "polygon", label: "Por polígono", help: "Si cae en un polígono de la tarifa de la tienda con un tier más caro que el de su CP, se cobra ese tier. El polígono nunca baja el precio ni da cobertura: el CP tiene que estar en la tarifa. Vale para el alta manual, la API de tiendas (también la cotización, si manda coordenadas) y la banda de zona de la etiqueta. Una cotización sin coordenadas sigue siendo por CP." },
          ]}
        />
        {!s.store_tariffs ? <div style={helpStyle}>Prendé «Tarifas de tiendas» para elegir.</div> : null}
      </div>

      <div style={sectionStyle}>
        <div style={headStyle}>3 · Liquidaciones</div>
        <Choice
          name="settlement_pricing" value={s.settlement_pricing} disabled={!anyTariff} onChange={(v) => set({ settlement_pricing: v })}
          options={[
            { value: "postal_code", label: "Por código postal (como siempre)" },
            { value: "polygon", label: "Por polígono", help: "Usa la ubicación que manda LightData, para las tarifas marcadas en el punto 1. Lo corregido a mano manda: con CP, dirección o zona corregidos, o Turbo, el polígono no se usa. Conviene cambiarlo al empezar una semana." },
          ]}
        />
        {!anyTariff ? <div style={helpStyle}>Marcá al menos un tipo de tarifa en el punto 1 para elegir.</div> : null}
      </div>

      <div style={sectionStyle}>
        <div style={headStyle}>4 · Chofer por zona</div>
        <Choice
          name="driver_mode" value={s.driver_mode} onChange={(v) => set({ driver_mode: v })}
          options={[
            { value: "off", label: "Apagado" },
            { value: "suggest", label: "Sugerir al armar el reparto", help: "En el detalle del reparto aparece el chofer habitual de donde caen los envíos y queda preseleccionado. Se asigna igual que siempre, con «Guardar chofer»." },
            { value: "drafts", label: "Sugerir y armar borradores", help: "Además, en Recorridos aparece «Armar borradores por zona»: un reparto por chofer sugerido con lo que está en las estanterías, sin asignar ni avisar a nadie. Alguien los revisa, asigna y larga." },
          ]}
        />
      </div>

      <div style={sectionStyle}>
        <div style={headStyle}>5 · De dónde sale el chofer habitual</div>
        <Choice
          name="driver_link" value={s.driver_link} disabled={s.driver_mode === "off"} onChange={(v) => set({ driver_link: v })}
          options={[
            { value: "zone", label: "De la zona", help: "Se carga en cada zona (acá, en Zonas)." },
            { value: "shelf", label: "De la estantería", help: "Se carga en cada estantería (Depósito → Estanterías)." },
            { value: "zone_first", label: "Zona primero; si no tiene, la estantería" },
            { value: "shelf_first", label: "Estantería primero; si no tiene, la zona" },
          ]}
        />
      </div>

      {error ? <div style={{ color: "var(--danger)" }}>{error}</div> : null}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
        <span style={helpStyle}>{s.updated_at ? `Última modificación: ${new Date(s.updated_at).toLocaleString("es-AR")}${s.updated_by ? ` · ${s.updated_by}` : ""}` : "Sin cambios desde que se instaló."}</span>
        <Button onClick={save} disabled={saving}>{saving ? "Guardando…" : "Guardar configuración"}</Button>
      </div>
    </div>
  );
}
